/**
 * Вкладка «Правила» раздела «Экология» панели администратора (ecology-survey, макет панели
 * администратора 16.09): «ответ клиента → документ в пульте». Таблица «Вопрос / Ответ / Документ /
 * Что делать / Условие», поиск по вопросу и документу, добавление и правка в модалке, удаление с
 * подтверждением. Стартовый набор — по логике опроса макета клиента; состав проверяет эколог.
 */
import { useState } from 'react'
import { ArrowRight, Pencil, Plus, Trash2, Workflow } from 'lucide-react'
import { adminApi, type EcoCatalogItem, type EcoQuestion, type EcoRule } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { ECO_RULE_ACTION_LABELS, ecoRuleConditionText } from '@/lib/ecology'
import { Panel } from '@/design-system/components/layout/panel'
import { Toolbar } from '@/design-system/components/layout/toolbar'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { Input } from '@/design-system/components/primitives/input'
import { ConfirmDialog } from '@/design-system/components/primitives/confirm-dialog'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'
import { RuleDialog } from './rule-dialog'

type DialogState = { open: false } | { open: true; rule: EcoRule | null }

export function RulesTab({
  rules,
  questions,
  catalog,
  onRulesChange,
}: {
  rules: EcoRule[]
  questions: EcoQuestion[]
  catalog: EcoCatalogItem[]
  onRulesChange: (rules: EcoRule[]) => void
}) {
  const [search, setSearch] = useState('')
  const [dialog, setDialog] = useState<DialogState>({ open: false })
  const [confirmDelete, setConfirmDelete] = useState<EcoRule | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const questionText = (id: string) =>
    questions.find((q) => q.id === id)?.text ?? 'удалённый вопрос'
  const itemName = (id: string | null) =>
    id ? (catalog.find((c) => c.id === id)?.name ?? 'позиция в архиве справочника') : '—'

  const query = search.trim().toLowerCase()
  const filtered = query
    ? rules.filter((r) =>
        [questionText(r.questionId), itemName(r.catalogItemId), r.explanation].some((t) =>
          t.toLowerCase().includes(query),
        ),
      )
    : rules

  async function remove() {
    if (!confirmDelete) return
    setError(null)
    setDeleting(true)
    try {
      await adminApi.ecology.deleteRule(confirmDelete.id)
      onRulesChange(rules.filter((r) => r.id !== confirmDelete.id))
      setConfirmDelete(null)
    } catch (err) {
      setConfirmDelete(null)
      setError(getErrorMessage(err, 'Не удалось удалить правило'))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div>
      <p className="mb-3 text-13 text-txt-secondary">
        По этим правилам кабинет после опроса по площадке добавляет ей документы и отчётность:
        «нужен» — красная строка «Отсутствует», «есть» — строка с датой из уточнения и рассчитанным
        сроком, «требует актуализации» — жёлтая строка у имеющегося документа. Правило срабатывает
        только на ответ, который клиент дал; незаполненное ничего не добавляет. Список —
        предложение, его проверяет эколог.
      </p>
      <Toolbar aria-label="Поиск и добавление правил">
        <div className="min-w-56 flex-1">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по вопросу или документу"
            aria-label="Поиск по вопросу или документу"
          />
        </div>
        <Button
          onClick={() => setDialog({ open: true, rule: null })}
          leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
        >
          Добавить правило
        </Button>
      </Toolbar>

      {error ? (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}

      <Panel>
        {filtered.length === 0 ? (
          <EmptyState
            icon={Workflow}
            title={rules.length === 0 ? 'Правил нет' : 'Ничего не найдено'}
            description={
              rules.length === 0
                ? 'Добавьте первое правило'
                : 'Измените запрос — поиск идёт по вопросу, документу и пояснению'
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <DataTable>
              <DataTableHead>
                <tr>
                  <DataTableHeaderCell>Вопрос</DataTableHeaderCell>
                  <DataTableHeaderCell>Ответ</DataTableHeaderCell>
                  <DataTableHeaderCell>
                    <span className="sr-only">Следствие</span>
                  </DataTableHeaderCell>
                  <DataTableHeaderCell>Документ</DataTableHeaderCell>
                  <DataTableHeaderCell>Что делать</DataTableHeaderCell>
                  <DataTableHeaderCell>Условие</DataTableHeaderCell>
                  <DataTableHeaderCell>{''}</DataTableHeaderCell>
                </tr>
              </DataTableHead>
              <DataTableBody>
                {filtered.map((r) => (
                  <DataTableRow key={r.id}>
                    <DataTableCell className="min-w-56 font-medium text-txt">
                      {questionText(r.questionId)}
                    </DataTableCell>
                    <DataTableCell>
                      <Badge variant="gray">{r.answer}</Badge>
                    </DataTableCell>
                    <DataTableCell className="text-txt-tertiary">
                      <ArrowRight className="size-4 stroke-current" aria-hidden />
                    </DataTableCell>
                    <DataTableCell className="min-w-48 text-txt">
                      {itemName(r.catalogItemId)}
                    </DataTableCell>
                    <DataTableCell className="min-w-48 text-txt-secondary">
                      {ECO_RULE_ACTION_LABELS[r.action]}
                      {r.explanation ? (
                        <div className="mt-0.5 text-12 text-txt-tertiary">{r.explanation}</div>
                      ) : null}
                    </DataTableCell>
                    <DataTableCell className="min-w-40 text-12 text-txt-secondary">
                      {ecoRuleConditionText(r, questions)}
                    </DataTableCell>
                    <DataTableCell className="text-right">
                      <div className="flex justify-end gap-1.5">
                        <Button
                          size="sm"
                          variant="secondary"
                          title="Изменить"
                          aria-label={`Изменить правило «${questionText(r.questionId)} = ${r.answer}»`}
                          onClick={() => setDialog({ open: true, rule: r })}
                        >
                          <Pencil className="size-4 stroke-current" aria-hidden />
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          title="Удалить"
                          aria-label={`Удалить правило «${questionText(r.questionId)} = ${r.answer}»`}
                          onClick={() => setConfirmDelete(r)}
                        >
                          <Trash2 className="size-4 stroke-current" aria-hidden />
                        </Button>
                      </div>
                    </DataTableCell>
                  </DataTableRow>
                ))}
              </DataTableBody>
            </DataTable>
          </div>
        )}
      </Panel>
      <p className="mt-2 text-12 text-txt-tertiary">Правил: {filtered.length}</p>

      <RuleDialog
        open={dialog.open}
        rule={dialog.open ? dialog.rule : null}
        questions={questions}
        catalog={catalog}
        onClose={() => setDialog({ open: false })}
        onSaved={(saved) => {
          const exists = rules.some((r) => r.id === saved.id)
          onRulesChange(
            exists ? rules.map((r) => (r.id === saved.id ? saved : r)) : [...rules, saved],
          )
          setDialog({ open: false })
        }}
      />

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Удаление правила"
        message={
          confirmDelete
            ? `Удалить правило «${questionText(confirmDelete.questionId)}» = ${confirmDelete.answer} → ${itemName(confirmDelete.catalogItemId)}?`
            : ''
        }
        onClose={() => setConfirmDelete(null)}
        closeDisabled={deleting}
        buttons={[
          {
            label: deleting ? 'Удаление…' : 'Удалить',
            variant: 'danger',
            disabled: deleting,
            onClick: () => void remove(),
          },
          {
            label: 'Отмена',
            variant: 'secondary',
            disabled: deleting,
            onClick: () => setConfirmDelete(null),
          },
        ]}
      />
    </div>
  )
}
